<!--
  TikTok 自动化规则管理 - 支持新旧两版配置格式

  主要修改（2026-04-16）：
  1. 新增模式切换：旧版（条件+动作）vs 新版（触发器+分支动作）
  2. 新版支持多分支规则：消耗达比例→延迟→ROAS对比→多动作分支
  3. 支持的触发器类型：立即检查、消耗比例+延迟+ROAS对比、消耗阈值
  4. 支持的动作类型：关停、修改预算、按比例调预算、不操作
  5. 默认分支只支持"不执行操作"
  6. 使用说明改为可折叠面板
-->

<template>
  <div class="campaign-rule-manager">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>TikTok 自动化规则管理</h2>
      <div class="header-actions">
        <el-button type="primary" icon="el-icon-plus" @click="handleCreate">新建规则</el-button>
        <el-button icon="el-icon-refresh" @click="loadRules">刷新</el-button>
      </div>
    </div>

    <!-- 规则列表 -->
    <el-card shadow="never" class="table-card">
      <el-table :data="ruleList" v-loading="loading" stripe height="100%" style="width: 100%; flex: 1;" :scrollbar-always-on="true">
        <el-table-column type="index" label="ID" width="60" align="center" >

          <template slot-scope="scope">
            <div class="rule-id">{{ scope.row.id }}</div>
          </template>
        </el-table-column>
        <el-table-column label="规则名称" min-width="180">
          <template slot-scope="scope">
            <div class="rule-name">{{ scope.row.ruleName }}</div>
          </template>
        </el-table-column>
        <el-table-column label="配置类型" width="120" align="center">
          <template slot-scope="scope">
            <el-tag  effect="plain" :type="scope.row.ruleConfig ? 'success' : 'info'" size="small">{{ scope.row.ruleConfig ? '新版' : '旧版' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="广告主ID" width="160" align="center">
          <template slot-scope="scope"><span style="font-size: 12px;">{{ scope.row.advertiserId }}</span></template>
        </el-table-column>
        <el-table-column label="监控间隔" width="120" align="center">
          <template slot-scope="scope">{{ scope.row.monitorInterval }} 分钟</template>
        </el-table-column>
<!--        <el-table-column label="动作类型" width="160" align="center">-->
<!--          <template slot-scope="scope">-->
<!--            <el-tag :type="getActionTypeColor(scope.row.actionType)" size="small">{{ getActionTypeText(scope.row.actionType) }}</el-tag>-->
<!--          </template>-->
<!--        </el-table-column>-->
        <el-table-column label="创建人" width="100" align="center">
          <template slot-scope="scope">{{ scope.row.createdBy || '-' }}</template>
        </el-table-column>
        <el-table-column label="状态" width="130" align="center">
          <template slot-scope="scope">
            <div class="switch-wrapper">
              <el-switch
                v-model="scope.row.enabled"
                active-color="#67c23a"
                inactive-color="#909399"
                :active-text="scope.row.enabled ? '启用' : ''"
                :inactive-text="scope.row.enabled ? '' : '禁用'"
                @change="handleToggle(scope.row)"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="上次执行" width="160" align="center">
          <template slot-scope="scope">{{ scope.row.lastExecuteTime ? formatDateTime(scope.row.lastExecuteTime) : '-' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="350" align="center">
          <template slot-scope="scope">
            <el-button size="mini" type="text" icon="el-icon-view" @click="handleViewConditions(scope.row)">查看条件</el-button>
            <el-button size="mini" type="text" icon="el-icon-document" @click="handleViewLogs(scope.row)">执行日志</el-button>
            <el-button size="mini" type="text" icon="el-icon-edit" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="text" icon="el-icon-delete" style="color: #f56c6c" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 新建/编辑规则对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="1000px" :close-on-click-modal="false">
      <el-tabs v-model="ruleMode" type="border-card">
        <!-- 旧版模式 -->
        <el-tab-pane label="旧版（条件+动作）" name="v1">
          <el-form ref="ruleFormV1" :model="ruleForm" :rules="rules" label-width="120px">
            <el-form-item label="规则名称" prop="ruleName"><el-input v-model="ruleForm.ruleName" placeholder="请输入规则名称" /></el-form-item>
            <el-form-item label="广告主" prop="advertiserId">
              <el-select v-model="ruleForm.advertiserId" placeholder="请选择广告主" filterable style="width: 100%">
                <el-option
                    v-for="item in advertiserList"
                    :key="item.id"
                    :label="item.advertiser_name.replace('-SUNNY-SINO-TT', '') "
                    :value="item.advertiser_id || item.advertiserId"
                />
              </el-select>
            </el-form-item>
            <el-form-item label="监控间隔" prop="monitorInterval">
              <el-input-number v-model="ruleForm.monitorInterval" :min="1" :max="1440" /><span class="form-tip">（1-1440分钟）</span>
            </el-form-item>
            <el-form-item label="监测时间范围">
              <el-date-picker v-model="timeRange" type="datetimerange" range-separator="至" start-placeholder="开始时间" end-placeholder="结束时间" value-format="yyyy-MM-dd HH:mm:ss" style="width: 100%" />
            </el-form-item>
            <el-divider content-position="left">触发条件</el-divider>
            <el-form-item label="条件组合逻辑">
              <el-radio-group v-model="ruleForm.conditionLogic">
                <el-radio label="AND">AND（所有条件都满足）</el-radio><el-radio label="OR">OR（任一条件满足）</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="条件列表">
              <div v-for="(condition, index) in ruleForm.conditionList" :key="index" class="condition-item">
                <el-row :gutter="10">
                  <el-col :span="7">
                    <el-select v-model="condition.field" placeholder="选择字段" @change="handleFieldChange(condition)">
                      <el-option-group label="基础字段">
                        <el-option label="消耗" value="spend" /><el-option label="ROAS" value="roas" />
                        <el-option label="点击次数" value="clicks" /><el-option label="点击率" value="ctr" />
                        <el-option label="转化数" value="conversion" /><el-option label="预算" value="budget" />
                      </el-option-group>
                      <el-option-group label="高级字段">
                        <el-option label="消耗占预算比例" value="spendToBudgetRatio" />
                        <el-option label="与昨日ROAS对比" value="roasVsYesterday" />
                        <el-option label="复合条件" value="custom" />
                      </el-option-group>
                    </el-select>
                  </el-col>
                  <el-col :span="5" v-if="condition.field !== 'custom'">
                    <el-select v-model="condition.operator" placeholder="运算符">
                      <el-option label="大于" value=">" /><el-option label="小于" value="<" />
                      <el-option label="大于等于" value=">=" /><el-option label="小于等于" value="<=" />
                      <el-option label="等于" value="=" /><el-option label="不等于" value="!=" />
                    </el-select>
                  </el-col>
                  <el-col :span="5" v-else style="display: flex; align-items: center; justify-content: center;">
                    <span style="color: #909399; font-size: 12px;">已配置</span>
                  </el-col>
                  <el-col :span="9">
                    <el-input-number v-if="condition.field !== 'custom'" v-model="condition.value" :precision="condition.field === 'spendToBudgetRatio' ? 2 : undefined" :step="condition.field === 'spendToBudgetRatio' ? 0.1 : undefined" :min="condition.field === 'spendToBudgetRatio' ? 0 : undefined" :max="condition.field === 'spendToBudgetRatio' ? 1 : undefined" style="width: 100%" />
                    <div v-else style="padding: 8px; background: #f0f9ff; border-radius: 4px; color: #1890ff; font-size: 13px;"><i class="el-icon-info"></i> 请在下方配置面板设置</div>
                  </el-col>
                  <el-col :span="3"><el-button type="danger" icon="el-icon-delete" circle size="mini" @click="removeCondition(index)" /></el-col>
                </el-row>
                <el-row v-if="condition.field === 'custom'" :gutter="10" style="margin-top: 10px; padding: 15px; background: #f5f7fa; border-radius: 6px;">
                  <el-col :span="8">
                    <div style="font-size: 12px; color: #606266; margin-bottom: 5px;">消耗比例阈值</div>
                    <el-input-number v-model="condition.spendRatio" :min="0.1" :max="1" :step="0.1" :precision="1" style="width: 100%" @change="updateCustomField(condition)" />
                  </el-col>
                  <el-col :span="8">
                    <div style="font-size: 12px; color: #606266; margin-bottom: 5px;">等待小时数</div>
                    <el-input-number v-model="condition.hoursAfter" :min="1" :max="72" style="width: 100%" @change="updateCustomField(condition)" />
                  </el-col>
                  <el-col :span="8">
                    <div style="font-size: 12px; color: #606266; margin-bottom: 5px;">ROAS对比值</div>
                    <el-input-number v-model="condition.value" :precision="2" :step="0.1" placeholder="-0.2" style="width: 100%" />
                  </el-col>
                </el-row>
              </div>
              <el-button type="primary" icon="el-icon-plus" size="small" @click="addCondition" style="margin-top: 10px">添加条件</el-button>
            </el-form-item>
            <el-divider content-position="left">执行动作</el-divider>
            <el-form-item label="动作类型" prop="actionType">
              <el-select v-model="ruleForm.actionType" placeholder="选择动作类型">
                <el-option label="关闭推广系列" value="DISABLE_CAMPAIGN" /><el-option label="修改预算" value="UPDATE_BUDGET" />
              </el-select>
            </el-form-item>
            <template v-if="ruleForm.actionType === 'UPDATE_BUDGET'">
              <el-form-item label="新预算" prop="newBudget"><el-input-number v-model="ruleForm.newBudget" :min="0" :precision="2" /></el-form-item>
              <el-form-item label="预算模式">
                <el-radio-group v-model="ruleForm.budgetMode">
                  <el-radio label="BUDGET_MODE_DAY">日预算</el-radio><el-radio label="BUDGET_MODE_TOTAL">总预算</el-radio>
                </el-radio-group>
              </el-form-item>
            </template>
            <el-form-item label="备注"><el-input v-model="ruleForm.remark" type="textarea" :rows="3" placeholder="请输入备注说明" /></el-form-item>
          </el-form>
        </el-tab-pane>

        <!-- 新版模式 -->
        <el-tab-pane label="新版（触发器+分支动作）" name="v2">
          <!-- 使用说明书 - 可折叠 -->
          <div class="guide-section">
            <div class="guide-header" @click="showGuide = !showGuide">
              <span><i :class="showGuide ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i> 新版规则使用说明</span>
              <span class="guide-hint">{{ showGuide ? '点击收起' : '点击展开' }}</span>
            </div>
            <div v-show="showGuide" class="guide-content">
              <el-alert type="info" :closable="false" show-icon style="margin-bottom: 15px;">
                <template slot="title"><strong>核心逻辑流程</strong></template>
                <div style="font-size: 13px; line-height: 1.8;">触发器决定何时检查 → 条件匹配决定执行哪个分支 → 分支动作决定如何处理</div>
              </el-alert>

              <el-collapse accordion style="margin-top: 15px;">
                <el-collapse-item title="触发器类型说明" name="1">
                  <ul style="margin: 5px 0; padding-left: 20px; font-size: 13px; line-height: 1.8;">
                    <li><strong>立即检查：</strong>每次监控周期都检查所有推广系列</li>
                    <li><strong>消耗达比例+延迟+ROAS对比：</strong>消耗达到预算X%后，等待Y小时，对比Z天的ROAS变化</li>
                    <li><strong>消耗超过固定金额：</strong>消耗达到指定金额后检查条件</li>
                  </ul>
                </el-collapse-item>

                <el-collapse-item title="分支条件字段说明" name="2">
                  <el-table :data="fieldInfo" size="mini" border style="font-size: 12px;">
                    <el-table-column prop="field" label="字段" width="140"></el-table-column>
                    <el-table-column prop="useCompare" label="使用对比基准" width="100" align="center">
                      <template slot-scope="scope">
                        <el-tag :type="scope.row.useCompare ? 'success' : 'info'" size="mini">{{ scope.row.useCompare ? '是' : '否' }}</el-tag>
                      </template>
                    </el-table-column>
                    <el-table-column prop="desc" label="说明"></el-table-column>
                  </el-table>
                  <div style="margin-top: 10px; font-size: 12px; color: #909399;">
                    <i class="el-icon-info"></i> 只有选择"ROAS变化比例"时才会使用对比基准计算变化率，其他字段直接比较当前值
                  </div>
                </el-collapse-item>

                <el-collapse-item title="典型应用场景" name="3">
                  <ul style="margin: 5px 0; padding-left: 20px; font-size: 13px; line-height: 1.8;">
                    <li><strong>场景1（使用变化比例）：</strong>消耗达预算80%后，6小时对比昨天ROAS → ROAS上升≥20%则加预算30%，ROAS下降≥30%则关停</li>
                    <li><strong>场景2（使用绝对值）：</strong>消耗达1000元后检查 → ROAS≥1.5加预算，ROAS&lt;0.5关停</li>

                  </ul>
                </el-collapse-item>

                <el-collapse-item title="对比基准说明" name="4">
                  <ul style="margin: 5px 0; padding-left: 20px; font-size: 13px; line-height: 1.8;">
                    <li><strong>昨天数据：</strong>与昨天同一时段的ROAS对比（推荐）</li>
                    <li><strong>近3天平均：</strong>与近3天平均ROAS对比（更平滑）</li>
                    <li><strong>近7天平均：</strong>与近7天平均ROAS对比（最平滑）</li>
                  </ul>
                </el-collapse-item>
              </el-collapse>
            </div>
          </div>

          <el-form ref="ruleFormV2" :model="ruleForm" :rules="rules" label-width="120px">
            <el-form-item label="规则名称" prop="ruleName"><el-input v-model="ruleForm.ruleName" placeholder="请输入规则名称" /></el-form-item>
            <el-form-item label="广告主" prop="advertiserId">
              <el-select v-model="ruleForm.advertiserId" placeholder="请选择广告主" filterable style="width: 100%">
                <el-option
                    v-for="item in advertiserList"
                    :key="item.id"
                    :label="item.advertiser_name.replace('-SUNNY-SINO-TT', '') +' | '+item.advertiser_id"
                    :value="item.advertiser_id || item.advertiserId"
                />
              </el-select>
            </el-form-item>  <el-form-item label="监控间隔" prop="monitorInterval">
              <el-input-number v-model="ruleForm.monitorInterval" :min="1" :max="1440" /><span class="form-tip">（1-1440分钟）</span>
            </el-form-item>
            <el-form-item label="监测时间范围">
              <el-date-picker v-model="timeRange" type="datetimerange" range-separator="至" start-placeholder="开始时间" end-placeholder="结束时间" style="width: 100%" />
            </el-form-item>

            <el-divider content-position="left">触发器配置</el-divider>

            <el-form-item label="触发器类型">
              <el-select v-model="ruleForm.triggerType" placeholder="选择触发器类型" style="width: 100%" @change="handleTriggerTypeChange">
                <el-option label="立即检查（每次监控都检查）" value="IMMEDIATE" />
                <el-option label="消耗达预算比例 + 延迟 + ROAS对比" value="SPEND_RATIO_DELAYED_ROAS_COMPARE" />
                <el-option label="消耗超过固定金额" value="SPEND_THRESHOLD" />
              </el-select>
            </el-form-item>

            <!-- 消耗比例触发器配置 -->
            <template v-if="ruleForm.triggerType === 'SPEND_RATIO_DELAYED_ROAS_COMPARE'">
              <el-form-item label="消耗比例阈值">
                <el-slider v-model="ruleForm.spendRatioThreshold" :min="0.1" :max="1" :step="0.05" :format-tooltip="val => val * 100 + '%'" />
                <span style="margin-left: 10px; color: #409eff;">{{ (ruleForm.spendRatioThreshold * 100).toFixed(0) }}%</span>
              </el-form-item>
              <el-form-item label="延迟小时数">
                <el-input-number v-model="ruleForm.delayHours" :min="1" :max="72" />
                <span class="form-tip">（达到阈值后等待X小时再检查）</span>
              </el-form-item>
              <el-form-item label="对比基准">
                <el-select v-model="ruleForm.compareBase" placeholder="选择对比基准" style="width: 100%">
                  <el-option label="昨天数据（推荐）" value="YESTERDAY" />
                  <el-option label="近3天平均（平滑波动）" value="AVG_3_DAYS" />
                  <el-option label="近7天平均（更平滑）" value="AVG_7_DAYS" />
                </el-select>
              </el-form-item>
            </template>

            <el-form-item label="消耗阈值" v-if="ruleForm.triggerType === 'SPEND_THRESHOLD'">
              <el-input-number v-model="ruleForm.spendThreshold" :min="1" :precision="2" />
              <span class="form-tip">（消耗达到此金额后触发检查）</span>
            </el-form-item>

            <el-divider content-position="left">分支动作配置</el-divider>

            <el-form-item label="分支列表">
              <div v-for="(branch, index) in ruleForm.branches" :key="index" class="branch-item">
                <el-card shadow="never" class="branch-card">
                  <div slot="header" class="branch-header">
                    <span>分支 {{ index + 1 }}：{{ branch.name || '未命名' }}</span>
                    <el-button type="danger" icon="el-icon-delete" size="mini" circle @click="removeBranch(index)" />
                  </div>

                  <el-form-item label="分支名称"><el-input v-model="branch.name" placeholder="如：ROAS上升表现优秀" /></el-form-item>

                  <el-form-item label="条件字段">
                    <el-select v-model="branch.conditionField" placeholder="选择字段" style="width: 100%">
                      <!-- 触发器为立即检查时，隐藏ROAS变化比例 -->
                      <el-option v-if="ruleForm.triggerType == 'SPEND_RATIO_DELAYED_ROAS_COMPARE'" label="ROAS变化比例（推荐）" value="roasChangeRatio" />
                      <el-option label="ROAS绝对值" value="roas" />
                      <el-option label="消耗金额" value="spend" />
                      <el-option label="预算" value="budget" />
                    </el-select>
                    <div v-if="ruleForm.triggerType === 'IMMEDIATE'" style="font-size: 12px; color: #909399; margin-top: 5px;">
                      <i class="el-icon-info"></i> 立即检查模式下只能使用绝对值比较，变化比例需要先达到消耗阈值
                    </div>
                  </el-form-item>

                  <el-form-item label="运算符">
                    <el-select v-model="branch.conditionOperator" placeholder="选择运算符" style="width: 100%">
                      <el-option label="大于" value=">" /><el-option label="大于等于" value=">=" />
                      <el-option label="小于" value="<" /><el-option label="小于等于" value="<=" />
                      <el-option label="等于" value="=" />
                    </el-select>
                  </el-form-item>

                  <el-form-item label="条件值">
                    <el-input-number v-model="branch.conditionValue" :precision="4" :step="0.1" style="width: 100%" />
                    <div style="font-size: 12px; color: #909399; margin-top: 5px;">
                      <template v-if="branch.conditionField === 'roasChangeRatio'">变化比例示例：0.2 = ROAS上升20%，-0.3 = ROAS下降30%</template>
                      <template v-else-if="branch.conditionField === 'roas'">ROAS绝对值，如：1.5 表示ROAS达到1.5</template>
                    </div>
                  </el-form-item>

                  <el-divider content-position="left">执行动作</el-divider>

                  <el-form-item label="动作类型">
                    <el-select v-model="branch.actionType" placeholder="选择动作类型" style="width: 100%">
                      <el-option label="不执行操作（仅记录日志）" value="NO_ACTION" />
                      <el-option label="关闭推广系列" value="DISABLE_CAMPAIGN" />
                      <el-option label="修改预算（固定值）" value="UPDATE_BUDGET" />
                      <el-option label="按比例调整预算" value="ADJUST_BUDGET_RATIO" />
                    </el-select>
                  </el-form-item>

                  <el-form-item label="预算值" v-if="branch.actionType === 'UPDATE_BUDGET'">
                    <el-input-number v-model="branch.budget" :min="0" :precision="2" style="width: 100%" />
                  </el-form-item>

                  <el-form-item label="调整比例" v-if="branch.actionType === 'ADJUST_BUDGET_RATIO'">
                    <el-slider v-model="branch.adjustRatio" :min="-0.9" :max="2" :step="0.05" :format-tooltip="val => (val * 100).toFixed(0) + '%'" />
                    <div style="text-align: center; color: #409eff;">
                      {{ (branch.adjustRatio * 100).toFixed(0) }}%
                      <span v-if="branch.adjustRatio >= 0" style="color: #67c23a;">（增加预算）</span>
                      <span v-if="branch.adjustRatio < 0" style="color: #f56c6c;">（减少预算）</span>
                    </div>
                  </el-form-item>
                </el-card>
              </div>
              <el-button type="primary" icon="el-icon-plus" size="small" @click="addBranch" style="margin-top: 10px">添加分支</el-button>
            </el-form-item>

            <!-- 默认分支：只保留"不执行操作" -->
            <el-form-item label="默认分支">
              <el-select v-model="ruleForm.defaultActionType" placeholder="所有分支都不匹配时执行" style="width: 100%">
                <el-option label="不执行操作（仅记录日志）" value="NO_ACTION" />
              </el-select>
              <span class="form-tip" style="display: block; margin-top: 5px;">当推广系列不满足任何分支条件时执行，默认只记录日志不执行操作</span>
            </el-form-item>

            <el-form-item label="备注"><el-input v-model="ruleForm.remark" type="textarea" :rows="3" placeholder="请输入备注说明" /></el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>

      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">确定</el-button>
      </div>
    </el-dialog>

    <!-- 查看条件对话框 -->
    <el-dialog title="规则条件详情" :visible.sync="conditionsDialogVisible" width="800px">
      <div v-if="currentRule">
        <p><strong>规则名称：</strong>{{ currentRule.ruleName }}</p>
        <p><strong>配置类型：</strong><el-tag :type="currentRule.ruleConfig ? 'success' : 'info'" size="small">{{ currentRule.ruleConfig ? '新版（触发器+分支）' : '旧版（条件+动作）' }}</el-tag></p>

        <!-- 新版规则：逻辑流程说明 -->
        <template v-if="currentRule.ruleConfig">
          <el-divider />
          <p><strong>执行逻辑流程：</strong></p>
          <div class="rule-flow-card">
            <div class="flow-step" v-for="(step, index) in getRuleFlowSteps(currentRule.ruleConfig)" :key="index">
              <div class="step-number">{{ index + 1 }}</div>
              <div class="step-content">
                <div class="step-title">{{ step.title }}</div>
                <div class="step-desc" v-html="step.desc"></div>
              </div>
            </div>
          </div>
          <el-divider />
          <p><strong>原始配置详情：</strong></p>
          <pre style="background: #f5f7fa; padding: 15px; border-radius: 4px; overflow-x: auto; max-height: 250px;">{{ formatJson(currentRule.ruleConfig) }}</pre>
        </template>

        <!-- 旧版规则 -->
        <template v-else>
          <el-divider />
          <p><strong>执行逻辑流程：</strong></p>
          <div class="rule-flow-card">
            <div class="flow-step">
              <div class="step-number">1</div>
              <div class="step-content">
                <div class="step-title">条件判断</div>
                <div class="step-desc">{{ getV1ConditionDesc(currentRule) }}</div>
              </div>
            </div>
            <div class="flow-step">
              <div class="step-number">2</div>
              <div class="step-content">
                <div class="step-title">执行动作</div>
                <div class="step-desc">{{ getActionTypeText(currentRule.actionType) }}{{ currentRule.actionType === 'UPDATE_BUDGET' ? '（新预算：¥' + currentRule.newBudget + '）' : '' }}</div>
              </div>
            </div>
          </div>
          <el-divider />
          <el-table :data="parseConditions(currentRule.conditions)" border>
            <el-table-column prop="field" label="字段" width="150"><template slot-scope="scope">{{ getFieldLabel(scope.row.field) }}</template></el-table-column>
            <el-table-column prop="operator" label="运算符" width="100"><template slot-scope="scope">{{ getOperatorText(scope.row.operator) }}</template></el-table-column>
            <el-table-column prop="value" label="值" />
          </el-table>
        </template>
      </div>
    </el-dialog>

    <!-- 执行日志对话框 -->
    <el-dialog title="规则执行日志" :visible.sync="logsDialogVisible" width="1200px">
      <el-table :data="logList" v-loading="logsLoading" stripe max-height="500">
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column label="推广系列" min-width="280">
          <template slot-scope="scope">
            <div class="campaign-info">
              <div class="campaign-name">{{ scope.row.campaignName }}</div>
              <div class="campaign-id">ID: {{ scope.row.campaignId }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="触发时数据" width="180" align="center">
          <template slot-scope="scope">
            <div class="trigger-data">
              <div class="data-item">
                <span class="data-label">消耗:</span>
                <span class="data-value spend">¥{{ scope.row.spend || 0 }}</span>
              </div>
              <div class="data-item">
                <span class="data-label">ROAS:</span>
                <span class="data-value roas">{{ scope.row.roas || 0 }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="动作" width="140" align="center">
          <template slot-scope="scope">
            <el-tag size="small" :type="getActionTypeColor(scope.row.actionType)">{{ getActionTypeText(scope.row.actionType) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="结果" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.result === 'SUCCESS' ? 'success' : 'danger'" size="small">
              <i :class="scope.row.result === 'SUCCESS' ? 'el-icon-check' : 'el-icon-close'"></i>
              {{ scope.row.result === 'SUCCESS' ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="消息" min-width="220">
          <template slot-scope="scope">
            <div class="message-cell" :title="scope.row.message">{{ scope.row.message }}</div>
          </template>
        </el-table-column>
        <el-table-column label="执行时间" width="160" align="center">
          <template slot-scope="scope">{{ formatDateTime(scope.row.executeTime) }}</template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import { getAllRules, createRule, updateRule, deleteRule, toggleRule, getRuleLogs } from '@/api/smartPlus'
import {advertiserApi} from "@/api/advertiserApi";

export default {
  name: 'CampaignRuleManager',
  data() {
    return {
      loading: false,
      submitting: false,
      ruleList: [],
      dialogVisible: false,
      dialogTitle: '新建规则',
      advertiserList: [],
      isEdit: false,
      ruleMode: 'v1',
      timeRange: [],
      // 使用说明折叠状态
      showGuide: false,
      // 字段说明表格数据
      fieldInfo: [
        { field: 'ROAS变化比例', useCompare: true, desc: '与对比基准计算变化率（上升/下降%）' },
        { field: 'ROAS绝对值', useCompare: false, desc: '直接使用当前ROAS值比较' },
        { field: '消耗金额', useCompare: false, desc: '直接使用当前消耗值比较' },
        { field: '预算', useCompare: false, desc: '直接使用当前预算值比较' }
      ],
      ruleForm: {
        id: null, ruleName: '', advertiserId: '', enabled: true, monitorInterval: 60,
        conditionLogic: 'AND', conditionList: [], actionType: 'DISABLE_CAMPAIGN',
        newBudget: null, budgetMode: 'BUDGET_MODE_DAY', remark: '',
        triggerType: 'IMMEDIATE', spendRatioThreshold: 0.8, delayHours: 6, compareBase: 'YESTERDAY',
        spendThreshold: 100, branches: [], defaultActionType: 'NO_ACTION'
      },
      rules: {
        ruleName: [{ required: true, message: '请输入规则名称', trigger: 'blur' }],
        advertiserId: [{ required: true, message: '请输入广告主ID', trigger: 'blur' }],
        monitorInterval: [{ required: true, message: '请设置监控间隔', trigger: 'blur' }]
      },
      conditionsDialogVisible: false,
      currentRule: null,
      logsDialogVisible: false,
      logsLoading: false,
      logList: []
    }
  },
  created() { this.loadRules()
    this.loadAdvertiserList()
  },
  methods: {

    loadAdvertiserList() {
      advertiserApi.getList().then(response => {
        if (response.code === 0 || response.success) {
          this.advertiserList = response.data?.list || []
        } else {
          console.error('加载广告主列表失败:', response.message)
        }
      }).catch(error => {
        console.error('请求广告主列表失败:', error)
      })
    },
    loadRules() {
      this.loading = true
      getAllRules().then(response => {
        if (response.code === 200) this.ruleList = response.data || []
        else this.$message.error(response.message || '加载失败')
      }).catch(error => { this.$message.error('请求失败: ' + error.message) })
          .finally(() => { this.loading = false })
    },

    handleCreate() {
      this.isEdit = false; this.dialogTitle = '新建规则'; this.ruleMode = 'v1'
      this.resetForm(); this.dialogVisible = true
    },

    handleEdit(row) {
      this.isEdit = true; this.dialogTitle = '编辑规则'
      if (row.ruleConfig) { this.ruleMode = 'v2'; this.parseV2FormData(row) }
      else { this.ruleMode = 'v1'; this.parseV1FormData(row) }
      this.timeRange = row.startTime && row.endTime ? [row.startTime, row.endTime] : []
      this.dialogVisible = true
    },

    parseV1FormData(row) {
      this.ruleForm = {
        id: row.id, ruleName: row.ruleName, advertiserId: row.advertiserId, enabled: row.enabled,
        monitorInterval: row.monitorInterval, conditionLogic: row.conditionLogic || 'AND',
        conditionList: this.parseConditions(row.conditions), actionType: row.actionType,
        newBudget: row.newBudget, budgetMode: row.budgetMode || 'BUDGET_MODE_DAY', remark: row.remark,
        triggerType: 'IMMEDIATE', spendRatioThreshold: 0.8, delayHours: 6, compareBase: 'YESTERDAY',
        spendThreshold: 100, branches: [], defaultActionType: 'NO_ACTION'
      }
    },

    parseV2FormData(row) {
      try {
        const config = JSON.parse(row.ruleConfig), trigger = config.trigger || {}, branches = config.branches || []
        this.ruleForm = {
          id: row.id, ruleName: row.ruleName, advertiserId: row.advertiserId, enabled: row.enabled,
          monitorInterval: row.monitorInterval, conditionLogic: row.conditionLogic || 'AND',
          conditionList: this.parseConditions(row.conditions), actionType: row.actionType,
          newBudget: row.newBudget, budgetMode: row.budgetMode || 'BUDGET_MODE_DAY', remark: row.remark,
          triggerType: trigger.type || 'IMMEDIATE',
          spendRatioThreshold: trigger.spendRatioThreshold || 0.8,
          delayHours: trigger.delayHours || 6, compareBase: trigger.compareBase || 'YESTERDAY',
          spendThreshold: trigger.spendThreshold || 100,
          branches: branches.map(b => ({
            name: b.name || '', conditionField: b.condition ? b.condition.field : '',
            conditionOperator: b.condition ? b.condition.operator : '>=',
            conditionValue: b.condition ? b.condition.value : 0,
            actionType: b.action ? b.action.type : 'NO_ACTION',
            budget: b.action ? b.action.budget : null,
            adjustRatio: b.action ? (b.action.adjustRatio || 0) : 0
          })),
          defaultActionType: config.defaultBranch ? config.defaultBranch.type : 'NO_ACTION'
        }
      } catch (e) { console.error('解析ruleConfig失败', e); this.parseV1FormData(row) }
    },

    handleSubmit() {
      const formRef = this.ruleMode === 'v1' ? 'ruleFormV1' : 'ruleFormV2'
      this.$refs[formRef].validate((valid) => {
        if (valid) {
          if (this.ruleMode === 'v2' && this.ruleForm.branches.length === 0) {
            this.$message.warning('请至少添加一个分支'); return
          }
          this.submitting = true
          const submitData = this.buildSubmitData()
          const apiCall = this.isEdit ? updateRule(this.ruleForm.id, submitData) : createRule(submitData)
          apiCall.then(response => {
            if (response.code === 200) { this.$message.success(this.isEdit ? '更新成功' : '创建成功'); this.dialogVisible = false; this.loadRules() }
            else this.$message.error(response.message || '操作失败')
          }).catch(error => { this.$message.error('请求失败: ' + error.message) })
              .finally(() => { this.submitting = false })
        }
      })
    },

    buildSubmitData() {
      const baseData = {
        ruleName: this.ruleForm.ruleName, advertiserId: this.ruleForm.advertiserId,
        enabled: this.ruleForm.enabled, monitorInterval: this.ruleForm.monitorInterval,
        startTime: this.timeRange && this.timeRange[0] ? this.timeRange[0] : null,
        createdBy: this.$store.state.user.username,
        endTime: this.timeRange && this.timeRange[1] ? this.timeRange[1] : null, remark: this.ruleForm.remark
      }
      if (this.ruleMode === 'v1') {
        return {
          ...baseData, conditionLogic: this.ruleForm.conditionLogic,
          conditions: JSON.stringify(this.ruleForm.conditionList), actionType: this.ruleForm.actionType,
          newBudget: this.ruleForm.actionType === 'UPDATE_BUDGET' ? this.ruleForm.newBudget : null,
          budgetMode: this.ruleForm.actionType === 'UPDATE_BUDGET' ? this.ruleForm.budgetMode : null
        }
      } else {
        const ruleConfig = {
          trigger: this.buildTrigger(),
          branches: this.ruleForm.branches.map(b => this.buildBranch(b)),
          defaultBranch: { type: this.ruleForm.defaultActionType }
        }
        return { ...baseData, ruleConfig: JSON.stringify(ruleConfig) }
      }
    },

    buildTrigger() {
      const trigger = { type: this.ruleForm.triggerType }
      if (this.ruleForm.triggerType === 'SPEND_RATIO_DELAYED_ROAS_COMPARE') {
        trigger.spendRatioThreshold = this.ruleForm.spendRatioThreshold
        trigger.delayHours = this.ruleForm.delayHours
        trigger.compareBase = this.ruleForm.compareBase
      } else if (this.ruleForm.triggerType === 'SPEND_THRESHOLD') {
        trigger.spendThreshold = this.ruleForm.spendThreshold
      }
      return trigger
    },

    buildBranch(branch) {
      const b = {
        name: branch.name,
        condition: { field: branch.conditionField, operator: branch.conditionOperator, value: branch.conditionValue },
        action: { type: branch.actionType }
      }
      if (branch.actionType === 'UPDATE_BUDGET') b.action.budget = branch.budget
      else if (branch.actionType === 'ADJUST_BUDGET_RATIO') b.action.adjustRatio = branch.adjustRatio
      return b
    },

    handleTriggerTypeChange() {
      if (this.ruleForm.triggerType === 'IMMEDIATE') {
        this.ruleForm.spendRatioThreshold = 0.8; this.ruleForm.delayHours = 6
        this.ruleForm.compareBase = 'YESTERDAY'; this.ruleForm.spendThreshold = 100
        // 切换为立即检查时，重置分支的字段为非变化比例类型
        this.ruleForm.branches.forEach(branch => {
          if (branch.conditionField === 'roasChangeRatio') branch.conditionField = 'roas'
        })
      }
    },

    addBranch() {
      this.ruleForm.branches.push({
        name: '', conditionField: 'roas', conditionOperator: '>=', conditionValue: 0.2,
        actionType: 'NO_ACTION', budget: null, adjustRatio: 0.3
      })
    },

    removeBranch(index) { this.ruleForm.branches.splice(index, 1) },

    handleDelete(row) {
      this.$confirm(`确定要删除规则 "${row.ruleName}" 吗？`, '提示', { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' })
          .then(() => {
            deleteRule(row.id).then(response => {
              if (response.code === 200) { this.$message.success('删除成功'); this.loadRules() }
              else this.$message.error(response.message || '删除失败')
            }).catch(error => { this.$message.error('请求失败: ' + error.message) })
          }).catch(() => {})
    },

    handleToggle(row) {
      toggleRule(row.id, row.enabled).then(response => {
        if (response.code === 200) this.$message.success(row.enabled ? '已启用' : '已禁用')
        else { row.enabled = !row.enabled; this.$message.error(response.message || '操作失败') }
      }).catch(error => { row.enabled = !row.enabled; this.$message.error('请求失败: ' + error.message) })
    },

    handleViewConditions(row) { this.currentRule = row; this.conditionsDialogVisible = true },

    handleViewLogs(row) {
      this.logsDialogVisible = true; this.logsLoading = true
      getRuleLogs(row.id).then(response => {
        if (response.code === 200) this.logList = response.data || []
        else this.$message.error(response.message || '加载失败')
      }).catch(error => { this.$message.error('请求失败: ' + error.message) })
          .finally(() => { this.logsLoading = false })
    },

    addCondition() { this.ruleForm.conditionList.push({ field: 'spend', operator: '>', value: 100 }) },

    handleFieldChange(condition) {
      switch(condition.field) {
        case 'spendToBudgetRatio': condition.value = 0.8; condition.operator = '>='; break
        case 'roasVsYesterday': condition.value = -0.3; condition.operator = '<'; break
        case 'custom': condition.spendRatio = 0.8; condition.hoursAfter = 6; condition.value = this.generateCustomFieldName(condition); condition.operator = '<'; break
        default: condition.value = 100; condition.operator = '>'
      }
    },

    removeCondition(index) { this.ruleForm.conditionList.splice(index, 1) },

    resetForm() {
      this.ruleForm = {
        id: null, ruleName: '', advertiserId: '', enabled: true, monitorInterval: 60,
        conditionLogic: 'AND', conditionList: [{ field: 'spend', operator: '>', value: 100 }],
        actionType: 'DISABLE_CAMPAIGN', newBudget: null, budgetMode: 'BUDGET_MODE_DAY', remark: '',
        triggerType: 'IMMEDIATE', spendRatioThreshold: 0.8, delayHours: 6, compareBase: 'YESTERDAY',
        spendThreshold: 100, branches: [], defaultActionType: 'NO_ACTION'
      }
      this.timeRange = []
      if (this.$refs.ruleFormV1) this.$refs.ruleFormV1.clearValidate()
      if (this.$refs.ruleFormV2) this.$refs.ruleFormV2.clearValidate()
    },

    parseConditions(conditionsStr) { if (!conditionsStr) return []; try { return JSON.parse(conditionsStr) } catch (e) { return [] } },

    updateCustomField(condition) { condition.value = this.generateCustomFieldName(condition) },
    generateCustomFieldName(condition) { return `roasVsYesterdayAfter${condition.spendRatio || 0.8}:${condition.hoursAfter || 6}` },

    getFieldLabel(field) {
      const map = { spend: '消耗', roas: 'ROAS', impressions: '展示次数', clicks: '点击次数', ctr: '点击率', conversion: '转化数', conversionValue: '转化价值', cpa: 'CPA', budget: '预算', spendToBudgetRatio: '消耗占预算比例', roasVsYesterday: '与昨日ROAS对比' }
      if (field.startsWith('roasVsYesterdayAfter')) {
        const match = field.match(/roasVsYesterdayAfter(\d+\.?\d*):(\d+)/)
        if (match) return `消耗达${parseFloat(match[1]) * 100}%后${match[2]}小时ROAS对比`
      }
      return map[field] || field
    },

    getActionTypeText(actionType) {
      const map = { DISABLE_CAMPAIGN: '关闭推广系列', UPDATE_BUDGET: '修改预算', ADJUST_BUDGET_RATIO: '按比例调预算', NO_ACTION: '不执行操作' }
      return map[actionType] || actionType
    },

    getActionTypeColor(actionType) {
      const map = { DISABLE_CAMPAIGN: 'danger', UPDATE_BUDGET: 'warning', ADJUST_BUDGET_RATIO: 'primary', NO_ACTION: 'info' }
      return map[actionType] || 'info'
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      const date = new Date(dateTime)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`
    },
// 获取规则流程步骤（查看条件用）
    getRuleFlowSteps(ruleConfig) {
      try {
        const config = typeof ruleConfig === 'string' ? JSON.parse(ruleConfig) : ruleConfig
        const trigger = config.trigger || {}
        const branches = config.branches || []
        const steps = []

        // 第1步：触发器
        let triggerDesc = ''
        switch (trigger.type) {
          case 'IMMEDIATE':
            triggerDesc = '每次监控周期都会检查所有推广系列的数据指标'
            break
          case 'SPEND_RATIO_DELAYED_ROAS_COMPARE': {
            const ratio = (trigger.spendRatioThreshold || 0.8) * 100
            const hours = trigger.delayHours || 6
            const base = this.getCompareBaseText(trigger.compareBase || 'YESTERDAY')
            triggerDesc = `当消耗达到预算的 ${ratio.toFixed(0)}% 后，等待 ${hours} 小时，然后与 <strong>${base}</strong> 的 ROAS 进行对比`
            break
          }
          case 'SPEND_THRESHOLD':
            triggerDesc = `当消耗达到 ¥${trigger.spendThreshold || 100} 后，检查条件是否满足`
            break
          default:
            triggerDesc = '未知触发器类型'
        }
        steps.push({ title: '第一步：触发器检测', desc: triggerDesc })

        // 第2步：分支判断
        if (branches.length > 0) {
          const branchDescs = branches.map((b, i) => {
            const fieldText = this.getFieldLabel(b.condition?.field || '')
            const opText = this.getOperatorText(b.condition?.operator || '>=')
            const valText = b.condition?.value
            const actionText = this.getActionTypeText(b.action?.type || 'NO_ACTION')
            let actionDetail = ''
            if (b.action?.type === 'UPDATE_BUDGET') actionDetail = `，修改预算为 ¥${b.action.budget}`
            if (b.action?.type === 'ADJUST_BUDGET_RATIO') actionDetail = `，按 ${((b.action.adjustRatio || 0) * 100).toFixed(0)}% 调整预算`
            return `${i + 1}. <strong>${b.name || '分支' + (i + 1)}</strong>：如果 <em>${fieldText}</em> ${opText} <em>${valText}</em>，则 <strong>${actionText}${actionDetail}</strong>`
          }).join('<br>')
          steps.push({ title: '第二步：分支条件匹配（按顺序执行第一个匹配的分支）', desc: branchDescs })
        }

        // 第3步：默认分支
        steps.push({ title: '第三步：默认分支（所有条件都不满足时）', desc: '不执行任何操作，只记录日志到执行历史' })

        return steps
      } catch (e) {
        return [{ title: '解析错误', desc: '无法解析规则配置，请检查JSON格式是否正确' }]
      }
    },

// 获取对比基准文本
    getCompareBaseText(base) {
      const map = { 'YESTERDAY': '昨天同一时段', 'AVG_3_DAYS': '近3天平均值', 'AVG_7_DAYS': '近7天平均值' }
      return map[base] || base
    },

// 获取运算符文本
    getOperatorText(op) {
      const map = { '>': '大于', '<': '小于', '>=': '大于等于', '<=': '小于等于', '=': '等于', '!=': '不等于' }
      return map[op] || op
    },

// 获取旧版条件描述
    getV1ConditionDesc(rule) {
      const conditions = this.parseConditions(rule.conditions)
      if (!conditions || conditions.length === 0) return '无条件限制'
      const logicText = rule.conditionLogic === 'AND' ? '所有条件同时满足' : '任一条件满足即可'
      const desc = conditions.map(c => `${this.getFieldLabel(c.field)} ${this.getOperatorText(c.operator)} ${c.value}`).join(` ${logicText} `)
      return `当 ${logicText} 时：${desc}`
    },
    formatJson(str) { try { return JSON.stringify(JSON.parse(str), null, 2) } catch (e) { return str } }
  }
}
</script>

<style scoped>
.campaign-rule-manager { 
  padding: 20px;
  height: calc(100vh - 85px);
  overflow: hidden;
}

.table-card {
  height: calc(100% - 60px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.table-card ::v-deep .el-card__body {
  height: 100%;
  padding: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
  z-index: 10;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
  color: #303133;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.header-actions { position: relative; z-index: 11; }
.header-actions .el-button { position: relative; z-index: 12; cursor: pointer; }
/* 表格样式优化 - 简约清爽风 */
.campaign-rule-manager ::v-deep .el-table {
  font-size: 13px;
  color: #303133;
}

.campaign-rule-manager ::v-deep .el-table th {
  background-color: #fafafa;
  color: #606266;
  font-weight: 500;
  font-size: 13px;
  border-bottom: 1px solid #e8e8e8;
}

.campaign-rule-manager ::v-deep .el-table td {
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.campaign-rule-manager ::v-deep .el-table__body tr:hover > td {
  background-color: #f9f9f9 !important;
}

.campaign-rule-manager ::v-deep .el-table--striped .el-table__body tr.el-table__row--striped td {
  background-color: #fafafa;
}

/* 规则名称样式 */
.rule-name {
  color: #2070c0;
  font-size: 11px;
  font-family: 微软雅黑, serif;
  //font-weight: 400;
  line-height: 1.5;
}

/* 规则ID样式 */
.rule-id {
  font-size: 11px;
  color: #a8a8a8;
  letter-spacing: 0.3px;
  line-height: 1.4;
}
.form-tip {
  margin-left: 8px;
  color: #a8a8a8;
  font-size: 12px;
  font-weight: 300;
}

.condition-item {
  margin-bottom: 10px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.branch-item { margin-bottom: 15px; }

.branch-card {
  border: 1px solid #e8e8e8;
  box-shadow: none;
}

.branch-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.text-small {
  font-size: 12px;
  color: #a8a8a8;
}

/* 按钮文字样式优化 */
.campaign-rule-manager ::v-deep .el-button--text {
  color: #606266;
  font-size: 12px;
  font-weight: 400;
  padding: 0 8px;
}

.campaign-rule-manager ::v-deep .el-button--text:hover {
  color: #409eff;
}

/* 分割线样式 */
.campaign-rule-manager ::v-deep .el-divider__text {
  font-size: 13px;
  font-weight: 500;
  color: #606266;
  background-color: #fff;
}

/* 使用说明书折叠样式 */
.guide-section {
  margin-bottom: 20px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

.guide-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background: #ecf5ff;
  cursor: pointer;
  font-weight: bold;
  color: #409eff;
}

.guide-header:hover {
  background: #d9ecff;
}

.guide-hint {
  font-size: 12px;
  color: #909399;
  font-weight: normal;
}

.guide-content {
  padding: 15px;
  background: #fff;
}
/* 查看条件-规则流程样式 */
.rule-flow-card {
  background: linear-gradient(135deg, #f0f9ff 0%, #e8f4fd 100%);
  border: 1px solid #d0e8fc;
  border-radius: 8px;
  padding: 15px 15px 10px;
}

.flow-step {
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
}

.flow-step:last-child {
  margin-bottom: 0;
}

.step-number {
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  flex-shrink: 0;
}

.step-content {
  margin-left: 12px;
  flex: 1;
  background: #fff;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #e4e7ed;
}

.step-title {
  font-weight: bold;
  color: #303133;
  margin-bottom: 6px;
  font-size: 13px;
}

.step-desc {
  color: #606266;
  font-size: 13px;
  line-height: 1.7;
}

/* 执行日志弹窗样式 */
.campaign-info {
  padding: 4px 0;
}

.campaign-name {
  font-weight: 500;
  color: #303133;
  font-size: 13px;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.campaign-id {
  font-size: 11px;
  color: #909399;
  font-family: 'Courier New', monospace;
}

.trigger-data {
  padding: 6px 0;
}

.data-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 0;
  font-size: 12px;
}

.data-label {
  color: #909399;
  margin-right: 8px;
}

/* 开关样式美化 */
.switch-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.switch-wrapper ::v-deep .el-switch {
  height: 24px;
  line-height: 24px;
}

.switch-wrapper ::v-deep .el-switch__label {
  font-size: 12px;
  font-weight: 500;
}

.switch-wrapper ::v-deep .el-switch__label--left {
  margin-right: 6px;
}

.switch-wrapper ::v-deep .el-switch__label--right {
  margin-left: 6px;
}

.switch-wrapper ::v-deep .is-checked .el-switch__core {
  border-color: #67c23a;
  background-color: #67c23a;
}

/* 方形开关样式 */
.switch-wrapper ::v-deep .el-switch__core {
  border-radius: 2px !important;
  width: 40px;
  height: 20px;
  padding: 0;
  box-sizing: border-box;
}

.switch-wrapper ::v-deep .el-switch__core::after {
  border-radius: 0 !important;
  width: 14px !important;
  height: 16px !important;
  margin-top: 1px !important;
  top: 0 !important;
  left: 2px !important;
}

.switch-wrapper ::v-deep .el-switch.is-checked .el-switch__core::after {
  margin-left: 22px !important;
}

.data-value {
  font-weight: 600;
  font-family: 'Courier New', monospace;
}

.data-value.spend {
  color: #f56c6c;
}

.data-value.roas {
  color: #67c23a;
}

.message-cell {
  font-size: 12px;
  color: #606266;
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}
</style>